import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import { Address } from '/@/modules/address/models/Address';
import { User } from '/@/modules/user/models/User';
import { CompanyPredefinedKey } from '@/modules/company/constants/CompanyPredefinedKey';
import { Team } from '@/modules/team/models/Team';
import { LocaleType } from '@/modules/user/constants/LocaleType';
import { CompanyService } from '@/modules/company/models/CompanyService';
import { PointItemCollection } from '@/modules/core/ui/models/PointItemCollection';
import { PointItem } from '@/modules/core/ui/models/PointItem';
import { FinancialInstitution } from '@/modules/predefined/models/FinancialInstitution';

export class Company extends BaseEntityModel {
  id: number;
  predefinedKey: string;
  name: string;
  frName?: string;
  website: string;
  phoneNumber: string;
  email?: string;
  isPartner?: boolean;
  stripeCadConnectedAccountId?: string;
  stripeUsdConnectedAccountId?: string;
  enConsentText?: string;
  frConsentText?: string;
  isClearEstate: boolean;
  estateConsentType?: string;
  hasPartnerServiceAgreementConsent?: boolean;
  enServiceAgreementLink?: string;
  frServiceAgreementLink?: string;
  currentAnnouncement?: string;

  address?: Relationship<Address> | null;
  users?: Relationship<User[]> | null;
  teams?: Relationship<Team[]> | null;
  companyServices?: Relationship<CompanyService[]> | null;
  financialInstitutions?: Relationship<FinancialInstitution[]> | null;
  fullLogoPath?: string | null;
  frenchFullLogoPath?: string | null;
  responsiveFullLogoPath?: string | null;
  logoUrl?: string;

  constructor(model: Partial<Company> = {}) {
    super(model);

    this.id = model.id as number;
    this.predefinedKey = model.predefinedKey as string;
    this.name = model.name as string;
    this.frName = model.frName;
    this.website = model.website as string;
    this.phoneNumber = model.phoneNumber ?? '';
    this.email = model.email;
    this.isPartner = model.isPartner;
    this.stripeCadConnectedAccountId = model.stripeCadConnectedAccountId;
    this.stripeUsdConnectedAccountId = model.stripeUsdConnectedAccountId;
    this.enConsentText = model.enConsentText;
    this.frConsentText = model.frConsentText;
    this.isClearEstate = model.isClearEstate as boolean;
    this.logoUrl = model.logoUrl;
    this.fullLogoPath = model.fullLogoPath;
    this.frenchFullLogoPath = model.frenchFullLogoPath;
    this.responsiveFullLogoPath = model.responsiveFullLogoPath;
    this.estateConsentType = model.estateConsentType;
    this.hasPartnerServiceAgreementConsent = model.hasPartnerServiceAgreementConsent;
    this.enServiceAgreementLink = model.enServiceAgreementLink;
    this.frServiceAgreementLink = model.frServiceAgreementLink;
    this.currentAnnouncement = model.currentAnnouncement;
    this.address = Address.mapModelOrNull(model.address);
    this.users = User.mapModelsOrNull(model.users);
    this.teams = Team.mapModelsOrNull(model.teams);
    this.companyServices = CompanyService.mapModelsOrNull(model.companyServices);
    this.financialInstitutions = FinancialInstitution.mapModelsOrNull(model.financialInstitutions);
  }

  getModelName(): ModelType {
    return ModelType.COMPANY;
  }

  get hasCustomLogo(): boolean {
    return !!this.fullLogoPath || !!this.responsiveFullLogoPath;
  }

  get isIGWealthManagement(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.IG_WEALTH_MANAGEMENT;
  }

  get isIAFinancialGroup(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.IA_FINANCIAL_GROUP;
  }

  get isSorrellPrivateTrust(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.SORRELL_PRIVATE_TRUST;
  }

  get isPlanMyEstate(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.PLAN_MY_ESTATE;
  }

  get firstLetters(): string {
    return this.name?.match(/[A-Z]/g).join('').slice(0, 2);
  }

  get logo(): string {
    return this.responsiveFullLogoPath ?? this.fullLogoPath ?? '';
  }

  get isEchelon(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.ECHELON;
  }

  get isPrairieCentreCreditUnion(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.PRAIRIE_CENTRE_CREDIT_UNION;
  }

  get isLaurentianBank(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.LAURENTIAN_BANK;
  }

  get isCainLamarre(): boolean {
    return this.predefinedKey === CompanyPredefinedKey.CAIN_LAMARRE;
  }

  get isB2BPartner(): boolean {
    return (
      this.isIGWealthManagement ||
      this.isIAFinancialGroup ||
      this.isSorrellPrivateTrust ||
      this.isPlanMyEstate ||
      this.isEchelon ||
      this.isPrairieCentreCreditUnion
    );
  }

  get canShowCompanyServiceAgreementConsent(): boolean {
    return !!this.hasPartnerServiceAgreementConsent && !!this.getServiceAgreementLinkByLocale();
  }

  getNameByLocale(locale?: string): string {
    return LocaleType.FRENCH === locale ? this.frName ?? this.name : this.name;
  }

  getLogoUrlByLocale(locale?: string): string {
    return LocaleType.FRENCH === locale
      ? this.frenchFullLogoPath ?? this.fullLogoPath
      : this.fullLogoPath;
  }

  getCompanyConsentByLocale(locale?: string): PointItem[] {
    return (
      PointItemCollection.mapFromText(
        LocaleType.FRENCH === locale ? this.frConsentText ?? this.enConsentText : this.enConsentText
      ).items ?? []
    );
  }

  getServiceAgreementLinkByLocale(locale?: string): string | undefined {
    return LocaleType.FRENCH === locale
      ? this.frServiceAgreementLink ?? this.enServiceAgreementLink
      : this.enServiceAgreementLink;
  }
}
